import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, ArrowRight, Check } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { money } from "../../lib/api";

/**
 * FloatingCartBar — Zomato / Swiggy / Myntra style "you've got something in
 * your bag" pill that sticks to the bottom of every storefront screen the
 * customer is browsing on, and stays out of the way on cart/checkout/account
 * pages.
 *
 * It has two visual states:
 *   1. "Just added" toast pill — appears for 2.5s after `add-to-cart` event,
 *      shows the line that was just added (image + qty + name).
 *   2. Persistent summary bar — shows item count + subtotal + "View bag →",
 *      links to /cart, respects the iOS safe-area-inset so it sits above
 *      the home indicator.
 *
 * Hidden when:
 *   - cart is empty
 *   - user is on /cart, /checkout/*, /account/*, /admin/*, /seller/*,
 *     /superadmin/*, /login, the order-success page, or any /admin route
 *   - the page is the order-success confirmation (cart will already be
 *     cleared, but belt-and-braces)
 */
const HIDE_ON_PREFIXES = [
  "/cart",
  "/checkout",
  "/account",
  "/admin",
  "/seller",
  "/superadmin",
  "/login",
  "/order/success",
  "/start-store",
];

export default function FloatingCartBar() {
  const { cart, cartCount } = useCart();
  const location = useLocation();
  const [justAdded, setJustAdded] = useState(null);

  // Listen for the `add-to-cart` custom event that ProductCard / ProductDetail
  // dispatch right after a successful add. Falls back gracefully when the
  // event doesn't fire (e.g. quantity update from /cart page itself).
  useEffect(() => {
    const onAdded = (e) => {
      setJustAdded(e.detail || { name: "Added to bag" });
      const t = setTimeout(() => setJustAdded(null), 2500);
      return () => clearTimeout(t);
    };
    window.addEventListener("kb:cart-added", onAdded);
    return () => window.removeEventListener("kb:cart-added", onAdded);
  }, []);

  // Hide on routes where a sticky cart bar would be redundant or
  // visually broken (admin chrome, checkout overlays, etc.)
  const path = location.pathname;
  const onHiddenRoute = HIDE_ON_PREFIXES.some(
    (p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`/t/`) && (() => {
      // For /t/{slug}/<anything> routes, strip the prefix and re-check
      const stripped = path.replace(/^\/t\/[^/]+/, "") || "/";
      return HIDE_ON_PREFIXES.some((q) => stripped === q || stripped.startsWith(`${q}/`));
    })(),
  );

  const subtotal = (cart.items || []).reduce(
    (s, i) => s + (i.product?.price || 0) * i.qty,
    0,
  );

  // Build the tenant-aware /cart URL so the bar works on path-based tenants too
  const cartHref = (() => {
    const m = path.match(/^\/t\/([^/]+)/);
    return m ? `/t/${m[1]}/cart` : "/cart";
  })();

  if (cartCount === 0 || onHiddenRoute) return null;

  return (
    <>
      {/* Slot at the bottom so the page content can scroll past it; height
          matches the pill so the customer can always reach the last row. */}
      <div className="h-20 md:h-16" aria-hidden="true" />

      <div
        className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
        data-testid="floating-cart-bar"
      >
        {/* Just-added toast pill (transient) */}
        {justAdded && (
          <div className="px-4 mb-2 flex justify-center pointer-events-auto" data-testid="floating-cart-toast">
            <div className="bg-[hsl(var(--kb-text))] text-white rounded-full pl-1 pr-4 py-1 shadow-2xl flex items-center gap-3 max-w-md animate-[kb-slide-up_220ms_ease-out]">
              <div className="w-9 h-9 rounded-full bg-[hsl(var(--kb-accent))]/20 flex items-center justify-center shrink-0">
                <Check size={16} className="text-[hsl(var(--kb-accent))]" />
              </div>
              <div className="text-xs leading-tight min-w-0 flex-1">
                <div className="font-medium truncate">Added to bag</div>
                <div className="opacity-70 truncate">{justAdded.name}</div>
              </div>
            </div>
          </div>
        )}

        {/* Persistent summary bar */}
        <div className="px-3 pointer-events-auto">
          <Link
            to={cartHref}
            className="mx-auto max-w-2xl bg-[hsl(var(--kb-primary))] text-white rounded-full shadow-2xl flex items-center justify-between gap-3 pl-4 pr-3 py-2.5 hover:opacity-95 active:scale-[0.98] transition-transform"
            data-testid="floating-cart-cta"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative shrink-0">
                <ShoppingBag size={20} />
                <span className="absolute -top-1.5 -right-1.5 bg-white text-[hsl(var(--kb-primary))] text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              </div>
              <div className="leading-tight min-w-0">
                <div className="text-xs uppercase tracking-wider opacity-80">
                  {cartCount} item{cartCount === 1 ? "" : "s"} in bag
                </div>
                <div className="font-medium text-sm" data-testid="floating-cart-subtotal">
                  {money(subtotal)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider whitespace-nowrap bg-white/15 rounded-full px-3 py-1.5">
              View bag <ArrowRight size={13} />
            </div>
          </Link>
        </div>
      </div>
    </>
  );
}
